// /vendor/team — vendor-admin-only team management.
//
// Reads through vendor_users via the vendor_admin_read RLS policy
// (vendor admins see same-vendor teammates). Mutations all route
// through the vendor-invite edge function (invite / set_role /
// revoke) — same polymorphic shape as portal-invite, the function
// carries last-admin protection and self-revoke prevention.
//
// Surfaces toast-level error messaging for the 409 paths; the function
// is the authority.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import { ShieldOff, Send, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

type Role = 'admin' | 'tech'

interface TeamRow {
  id: string
  role: Role
  full_name: string | null
  email: string | null
  last_login_at: string | null
  created_at: string
}

const ROLE_COPY: Record<Role, { label: string; color: string; bg: string; border: string; one_liner: string }> = {
  admin: { label: 'Admin', color: 'var(--accent-text)',  bg: 'var(--accent-dim)', border: 'var(--accent-border)', one_liner: 'Manages the team, sees everything.' },
  tech:  { label: 'Tech',  color: 'var(--info)',         bg: 'var(--info-dim)',   border: 'var(--info-border)',   one_liner: 'Sees and works jobs.' },
}

export default function VendorTeam() {
  const { vendorUser } = useAuth()
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)

  const { data: team = [], isLoading } = useQuery({
    queryKey: ['vendor-team', vendorUser?.vendor_id],
    enabled: !!vendorUser?.vendor_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_users')
        .select('id, role, full_name, email, last_login_at, created_at')
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as TeamRow[]
    },
  })

  const invokeFn = async (body: Record<string, unknown>): Promise<{ data: any; error: string | null }> => {
    const { data, error } = await supabase.functions.invoke<any>('vendor-invite', { body })
    if (error) {
      const ctx = (error as { context?: { text?: () => Promise<string> } }).context
      let msg = error.message
      if (ctx && typeof ctx.text === 'function') {
        try { const text = await ctx.text(); const parsed = JSON.parse(text); msg = parsed?.error ?? msg }
        catch { /* keep default */ }
      }
      return { data: null, error: msg }
    }
    return { data, error: null }
  }

  const setRoleMutation = useMutation({
    mutationFn: async (vars: { vendor_user_id: string; role: Role }) => {
      const r = await invokeFn({ action: 'set_role', ...vars })
      if (r.error) throw new Error(r.error)
      return r.data
    },
    onSuccess: () => {
      toast.success('Role updated')
      queryClient.invalidateQueries({ queryKey: ['vendor-team', vendorUser?.vendor_id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const revokeMutation = useMutation({
    mutationFn: async (vendor_user_id: string) => {
      const r = await invokeFn({ action: 'revoke', vendor_user_id })
      if (r.error) throw new Error(r.error)
      return r.data
    },
    onSuccess: () => {
      toast.success('Access revoked')
      queryClient.invalidateQueries({ queryKey: ['vendor-team', vendorUser?.vendor_id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Team</h1>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ink-tertiary)' }}>Manage who at your business can sign in.</p>
      </header>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Members ({team.length})
          </p>
          {!inviteOpen && (
            <button type="button" onClick={() => setInviteOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Send style={{ width: 12, height: 12 }} />
              Invite
            </button>
          )}
        </div>

        {inviteOpen && (
          <InviteForm onClose={() => setInviteOpen(false)} onInvited={() => {
            queryClient.invalidateQueries({ queryKey: ['vendor-team', vendorUser?.vendor_id] })
            setInviteOpen(false)
          }} invoke={invokeFn} />
        )}

        {isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'center', padding: '24px 0' }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {team.map(member => (
              <MemberRow key={member.id} member={member}
                selfId={vendorUser?.id ?? ''}
                onSetRole={(role) => setRoleMutation.mutate({ vendor_user_id: member.id, role })}
                onRevoke={() => {
                  if (confirm(`Revoke vendor portal access for ${member.full_name || member.email}? They will be signed out immediately.`))
                    revokeMutation.mutate(member.id)
                }}
                pending={setRoleMutation.isPending || revokeMutation.isPending} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function MemberRow({ member, selfId, onSetRole, onRevoke, pending }: {
  member: TeamRow; selfId: string;
  onSetRole: (role: Role) => void; onRevoke: () => void; pending: boolean
}) {
  const cfg = ROLE_COPY[member.role]
  const isSelf = member.id === selfId
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>
            {member.full_name || member.email || '—'}
            {isSelf && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', marginLeft: 6 }}>(you)</span>}
          </p>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {cfg.label}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
          {member.email}
          {member.last_login_at ? ` · last active ${formatRelativeDate(member.last_login_at)}` : ' · invited, not signed in yet'}
        </p>
      </div>
      <select value={member.role} onChange={e => onSetRole(e.target.value as Role)} disabled={pending}
        style={{ padding: '6px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 11, fontWeight: 600, color: 'var(--ink-primary)', cursor: 'pointer' }}>
        <option value="admin">Admin</option>
        <option value="tech">Tech</option>
      </select>
      <button type="button" onClick={onRevoke} disabled={pending || isSelf}
        title={isSelf ? "You can't revoke your own access" : 'Revoke access'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'transparent', border: '1px solid var(--danger-border)', borderRadius: 7, color: 'var(--danger)', fontSize: 11, fontWeight: 600, cursor: pending || isSelf ? 'not-allowed' : 'pointer', opacity: isSelf ? 0.5 : 1 }}>
        <ShieldOff style={{ width: 11, height: 11 }} />
        Revoke
      </button>
    </div>
  )
}

function InviteForm({ onClose, onInvited, invoke }: {
  onClose: () => void; onInvited: () => void;
  invoke: (body: Record<string, unknown>) => Promise<{ data: any; error: string | null }>
}) {
  const [form, setForm] = useState({ full_name: '', email: '', role: 'tech' as Role })
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (!form.email.trim() || !form.full_name.trim()) {
      toast.error('Email and name are required')
      return
    }
    setBusy(true)
    const r = await invoke({ action: 'invite', email: form.email.trim(), full_name: form.full_name.trim(), role: form.role })
    setBusy(false)
    if (r.error) { toast.error(r.error); return }
    toast.success('Invite sent')
    onInvited()
  }

  return (
    <form onSubmit={onSubmit}
      style={{ background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
      <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Full name *" style={inp} />
      <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@company.com *" type="email" style={inp} />
      <div>
        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Role</label>
        <select value={form.role} onChange={e => set('role', e.target.value as Role)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="admin">Admin — manages the team, sees everything</option>
          <option value="tech">Tech — sees and works jobs</option>
        </select>
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>
          <AlertCircle style={{ width: 10, height: 10, display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
          {ROLE_COPY[form.role].one_liner}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onClose} disabled={busy}
          style={{ flex: 1, padding: '9px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button type="submit" disabled={busy}
          style={{ flex: 1, padding: '9px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {busy ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 12, height: 12 }} />}
          {busy ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  )
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px', background: 'var(--field-bg)',
  border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13,
  color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box',
}
